import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAuth } from 'store/auth/hooks'
import { referralApi } from 'services/referralService'
import {
  clearStoredReferral,
  getStoredReferral,
  markClaimTried,
  referralFromLocation,
  storeReferral,
  wasClaimTriedBy,
} from 'utils/referralStorage'

// Claim errors that mean the ref itself is unusable. Anything else (account too old,
// already referred) only rules out the ACCOUNT claim — the ref still prefills the
// create-community referral field, which is how existing organizers get attributed.
const DEAD_REF_CODES = new Set(['INVALID_CODE', 'SELF_REFERRAL'])

/**
 * Global, renders nothing. Mounted once inside <Router> (router.tsx).
 *
 * 1. Captures `/r/:ref` and `?ref=` into localStorage (first touch wins).
 *    The initial URL is read synchronously in a useState initializer because
 *    `/` → `<Navigate to=Hub>` would otherwise drop `?ref=` before an effect runs.
 * 2. Once a user is signed in, claims the stored referral once per user. The
 *    backend decides eligibility (new accounts only, no self-referral, write-once).
 *    Storage is cleared only on success or a dead ref (unknown / own code); an
 *    ineligible account keeps the ref for the create-community form.
 */
export const ReferralCapture = () => {
  useState(() => {
    storeReferral(referralFromLocation(window.location))
    return null
  })

  const location = useLocation()
  useEffect(() => {
    storeReferral(referralFromLocation(location))
  }, [location])

  const { user } = useAuth()
  const attempted = useRef<string | null>(null)

  useEffect(() => {
    const userId = user?._id
    const ref = getStoredReferral()
    if (!userId || !ref) return
    // Already referred, or this user's claim was already refused: keep the ref
    // (community-creation prefill) but don't re-send a claim that can't succeed.
    if (user?.referral?.referredBy || wasClaimTriedBy(userId)) return
    const key = `${userId}:${ref}`
    if (attempted.current === key) return
    attempted.current = key

    referralApi
      .claim(ref)
      .then((res) => {
        clearStoredReferral()
        const name = res?.referrer?.username
        toast.success(name ? `You joined Cyberlete via ${name}'s invite 🎮` : 'Referral applied')
      })
      .catch((err) => {
        const status = err?.response?.status
        if (!status || status === 429 || status >= 500) return // transient — retry next load
        if (DEAD_REF_CODES.has(err?.response?.data?.code)) clearStoredReferral()
        else markClaimTried(userId)
      })
  }, [user?._id, user?.referral?.referredBy])

  return null
}

export default ReferralCapture
